import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { ConfirmDialog, type ConfirmDialogVariant } from '../components/ConfirmDialog';

type ConfirmOptions = {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
};

type InternalState = ConfirmOptions & {
  open: boolean;
  isLoading: boolean;
};

type ConfirmDialogContextValue = {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
};

const ConfirmDialogContext = createContext<ConfirmDialogContextValue | undefined>(undefined);

type ProviderProps = {
  children: ReactNode;
};

export function ConfirmDialogProvider({ children }: ProviderProps) {
  const [state, setState] = useState<InternalState>({
    open: false,
    isLoading: false,
    title: '',
    description: undefined,
    confirmLabel: 'Confirmar',
    cancelLabel: 'Cancelar',
    variant: 'info',
  });
  const [resolver, setResolver] = useState<((value: boolean) => void) | null>(null);

  const close = useCallback(
    (result: boolean) => {
      setState((prev) => ({ ...prev, open: false, isLoading: false }));
      if (resolver) {
        resolver(result);
        setResolver(null);
      }
    },
    [resolver],
  );

  const confirm = useCallback((options: ConfirmOptions) => {
    return new Promise<boolean>((resolve) => {
      setResolver(() => resolve);
      setState({
        open: true,
        isLoading: false,
        title: options.title,
        description: options.description,
        confirmLabel: options.confirmLabel ?? 'Confirmar',
        cancelLabel: options.cancelLabel ?? 'Cancelar',
        variant: options.variant ?? 'info',
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    close(true);
  }, [close]);

  const handleCancel = useCallback(() => {
    close(false);
  }, [close]);

  return (
    <ConfirmDialogContext.Provider value={{ confirm }}>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.title}
        description={state.description}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        variant={state.variant}
        isLoading={state.isLoading}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext);
  if (!ctx) {
    throw new Error('useConfirmDialog deve ser usado dentro de ConfirmDialogProvider');
  }
  return ctx;
}

